/**
 * Blocks render until auth, preferences, and critical interface elements are loaded.
 */
import * as React from 'react';
import { useAuthStore } from '@/stores/authStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useInterfaceElementsStore } from '@/stores/interfaceElementsStore';
import { Spinner } from '@/components/ui/spinner';

/** Props for {@link RbacLoader}. */
interface RbacLoaderProps {
  children: React.ReactNode;
}

/** Gate children until preferences plus sidebar/dashboard interface elements are ready. */
export const RbacLoader: React.FC<RbacLoaderProps> = ({ children }) => {
  const { isAuthenticated, roles } = useAuthStore();
  const { isLoaded: arePreferencesLoaded } = usePreferencesStore();
  const interfaceStore = useInterfaceElementsStore();

  const areCriticalElementsLoaded =
    interfaceStore.isInterfaceLoaded('sidebar') && interfaceStore.isInterfaceLoaded('dashboard');

  const isLoading = !arePreferencesLoaded || !areCriticalElementsLoaded;
  const hasRoles = roles && roles.length > 0;

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (!hasRoles) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Spinner variant="default" size="xl" label="Chargement des rôles..." />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Spinner variant="default" size="xl" label="Chargement de l'interface..." />
      </div>
    );
  }

  return <>{children}</>;
};
